import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { type Renderable } from '@adornis/chemistry/renderable.js';
import { animate } from '@lit-labs/motion';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { concatMap, firstValueFrom, timer } from 'rxjs';
import { TailwindScope } from '../TailwindScope.js';

/**
 * @element sg-snackbar
 */
@customElement('sg-snackbar')
export class SgSnackbar extends XSnackbar {
  protected static override snackbarTag: SgSnackbar;

  private errorMode = false;

  override render() {
    return html`
      <div wrapper ${animate({ properties: ['translate', 'opacity'], onComplete: () => this._completeHide() })}>
        ${TailwindScope.render({ content: this._renderContent() })}
      </div>
    `;
  }

  static override async show(
    content: Renderable | ((instance: XSnackbar) => Renderable),
    durationMS = 6000,
    minDurationMS = 1000,
  ) {
    this.ensureInit(false);
    const autoHide = typeof content !== 'function';
    this.queueSource.next({ content, durationMS, minDurationMS, autoHide });
  }

  static async showError(
    content: Renderable | ((instance: XSnackbar) => Renderable),
    durationMS = 6000,
    minDurationMS = 1000,
  ) {
    this.ensureInit(true);
    const autoHide = typeof content !== 'function';
    this.queueSource.next({ content, durationMS, minDurationMS, autoHide });
  }

  protected static override ensureInit(errorMode: boolean = false) {
    if (this.hasBeenInitialized) {
      this.snackbarTag.errorMode = errorMode;
      return;
    }
    this.hasBeenInitialized = true;

    this.snackbarTag = document.createElement('sg-snackbar') as SgSnackbar;
    this.snackbarTag.errorMode = errorMode;

    this.queueSource
      .pipe(
        concatMap(async instruction => {
          document.body.appendChild(this.snackbarTag);
          await this.snackbarTag.hide();

          requestAnimationFrame(() => {
            this.snackbarTag._showElement(instruction);
          });

          await firstValueFrom(timer(instruction.minDurationMS));
        }),
      )
      .subscribe();
  }

  override styles() {
    return [
      ...super.styles(),
      {
        // shadow DOM without Tailwind: the former x-flex column layout lives here
        '[wrapper]': {
          display: 'flex',
          flexDirection: 'column',
          background: this.errorMode ? this.colors.tone.error : this.colors.dark,
        },
      },
    ];
  }
}
